import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../Common/Layout";
import axios from "axios";
import { Toast } from "primereact/toast";
import { formatDate, toInputDateFormat, formatPhoneNumberDash, formatDateOfBirth } from "../utils/dateFormat";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Reusable input field for 'Property Insurance'
function PropertyInsuranceField({ value, onChange, name = "propertyInsurance" }: { value: string; onChange: (e: any) => void; name?: string }) {
  return (
    <div className="form-group">
      <label>Property Insurance</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );
}

function ReasonForNoInsuranceDropdown({
  value,
  onChange,
  state,
  error,
}: {
  value: string;
  onChange: (e: any) => void;
  state: string;
  error?: boolean;
}) {
  return (
    <select name="reasonForNoInsurance" className={`form-input compact-select ${error ? "error" : ""}`} value={value} onChange={onChange}>
      <option value="">Select reason</option>
      {state === "FL" &&
        REASON_FOR_NO_INSURANCE_FLORIDA.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      {state !== "FL" &&
        REASON_FOR_NO_INSURANCE_DEFAULT.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
    </select>
  );
}

// State abbreviation to full name mapping - moved outside component for stability
const stateAbbreviationMap: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

// State name to abbreviation mapping for API calls
const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

const PRIOR_CARRIERS = [
  "21st CENTURY INS CO OF THE SOUTHWEST",
  "21ST CENTURY INS NATIONAL INS CO. INC",
  "21st CENTURY INSURANCE COMPANY",
  "AAA TEXAS COUNTY MUTUAL INSURANCE CO",
  "ACA INSURANCE COMPANY",
  "ACUITY",
  "AETNA",
  "AFFIRMATIVE INSURANCE COMPANY",
  "AGRICULTURAL WORKERS MUTUAL AUTO INS CO",
  "AIGM",
  "AIU INS CO",
  "ALFA",
  "ALFA VISION INSURANCE CORP",
  "All Other Non Standard Companies",
  "All Other Non Voluntary Companies",
  "All Other Standard Companies",
  "ALLIED AUTO",
  "ALLIED PROPERTY & CASUALTY INS CO",
  "ALLMERICA FINANCIAL ALLIANCE INS CO",
  "ALLSTATE COUNTY MUTUAL INSURANCE CO",
  "ALLSTATE FIRE & CASUALTY INSURANCE CO",
  "ALLSTATE INDEMNITY CO",
  "ALLSTATE INS CO (NOT INDEMNITY)",
  "ALLSTATE PROP AND CAS INS CO",
  "ALPHA PROPERTY & CASUALTY INSURANCE CO",
  "AMCO INS CO",
  "AMERICA FIRST INSURANCE CO",
  "AMERICAN AMBASSADOR",
  "AMERICAN BANKERS INS CO OF FL",
  "AMERICAN COMMERCE INSURANCE CO",
  "AMERICAN ECONOMY INS CO",
  "AMERICAN FAMILY HOME INS CO",
  "AMERICAN FAMILY INS CO",
  "AMERICAN FAMILY MUTUAL",
  "AMERICAN FIRE & CASUALTY COMPANY",
  "AMERICAN HOME ASSURANCE COMPANY",
  "AMERICAN INDEPENDENT",
  "AMERICAN INS CO",
  "AMERICAN INTERNATIONAL INS CO",
  "AMERICAN INTERNATIONAL SOUTH INS CO",
  "AMERICAN MANUFACTURERS MUTUAL INS CO",
  "AMERICAN MERCURY INS CO",
  "AMERICAN MODERN HOME INS CO",
  "AMERICAN NATIONAL COUNTY MUTUAL INS CO",
  "AMERICAN NATIONAL GENERAL INS CO",
  "AMERICAN NATIONAL PROP AND CAS CO",
  "AMERICAN PREMIER INS CO",
  "AMERICAN RELIABLE INS CO",
  "AMERICAN SELECT INS CO",
  "AMERICAN SERVICE INS CO",
  "AMERICAN SERVICE PATRIOT",
  "AMERICAN STANDARD INS",
  "AMERICAN STANDARD INS CO OF OH",
  "AMERICAN STANDARD INS CO OF WI",
  "AMERICAN STATES INS CO",
  "AMERICAN STATES PREFERRED INS CO",
  "AMERICAN UNDERWRITERS INS CO",
  "AMERICAN WESTERN HOME INS CO",
  "AMEX ASSURANCE COMPANY",
  "AMICA MUTUAL INS CO",
  "ASSIGNED RISK PLAN",
  "ATLANTA CASUALTY CO",
  "ATLANTA SPECIALTY INS CO",
  "AUDUBON",
  "AUSTIN MUTUAL INS CO",
  "AUTO CLUB FAMILY INS CO (AAA)",
  "AUTO-OWNERS INS CO",
  "AUTOMOBILE CLUB INTERINSURANCE EXCH-AAA",
  "AUTOMOBILE INS CO OF HARTFORD",
  "BEACON NATIONAL INS CO",
  "BIRMINGHAM FIRE INSURANCE CO OF PA",
  "BOSTON OLD COLONY INS CO",
  "BRISTOL WEST INS CO",
  "BRISTOL WEST/COAST NATIONAL",
  "BUCKEYE STATE MUTUAL INS CO",
  "CALIFORNIA CASUALTY INDEMNITY EXCHANGE",
  "CALIFORNIA CASUALTY INS CO",
  "CAMERON MUTUAL INS CO",
  "CELINA",
  "CENTRAL MUTUAL INS CO",
  "CHARTER OAK FIRE INS CO",
  "CHUBB LLOYDS INS CO OF TX",
  "CIGNA SPECIALTY INS CO",
  "CINCINNATI INS CO",
  "CITIZENS INS CO OF AMERICA",
  "CITIZENS INS CO OF MIDW",
  "CLARENDON",
  "CNA INS CO",
  "CNL INSURANCE",
  "COAST NATIONAL",
  "COLONIAL COUNTY MUTUAL INS CO",
  "COLORADO CASUALTY INS CO",
  "COLORADO FARM BUREAU MUTUAL",
  "COLUMBIA MUTUAL INS CO",
  "COLUMBIA NATIONAL INS CO",
  "CONSUMERS COUNTY MUTUAL INS CO",
  "CONTINENTAL CASUALTY",
  "CONTINENTAL INS CO",
  "CORNERSTONE NATIONAL INS CO",
  "COTTON STATES",
  "COUNTRY MUTUAL INS CO",
  "COUNTRY PREFERRED INS CO",
  "COUNTRYWAY INS CO",
  "DAIRYLAND COUNTY MUTUAL INS CO",
  "DAIRYLAND INS CO",
  "DEERBROOK INS CO",
  "DEPOSITORS INS CO",
  "DIRECT GENERAL INS CO",
  "DIRECT GENERAL INS CO OF MS",
  "DIRECT INSURANCE",
  "ECONOMY PREMIER ASSURANCE",
  "ELECTRIC INS CO",
  "EMCASCO INS CO",
  "EMPIRE FIRE AND MARINE INS CO",
  "EMPLOYERS FIRE INS CO",
  "EMPLOYERS MUTUAL CASUALTY CO",
  "ENCOMPASS INDEMNITY CORP",
  "ENCOMPASS PROPERTY AND CAS CO",
  "EQUITY INS CO",
  "ERIE INSURANCE COMPANY",
  "ERIE INSURANCE EXCHANGE",
  "ESURANCE INS CO",
  "FARMERS INS EXCHANGE",
  "FEDERAL INS CO",
  "FIDELITY AND CASUALTY OF NY",
  "FIREMANS FUND INS CO",
  "FOREMOST INS CO GRAND RAPIDS MI",
  "FOUNDERS INS CO",
  "GEICO CASUALTY CO",
  "GEICO GENERAL INS CO",
  "GEICO INDEMNITY",
  "GREAT AMERICAN INS CO",
  "HARTFORD FIRE INS CO",
  "LIBERTY MUTUAL FIRE INS CO",
  "NATIONAL GENERAL INS",
  "NATIONWIDE MUTUAL INS CO",
  "OHIO CASUALTY INS CO",
  "PROGRESSIVE INSURANCE",
  "SAFECO INS CO OF AMERICA",
  "STATE FARM MUTUAL AUTOMOBILE INS CO",
  "TRAVELERS CASUALTY COMPANY OF CT",
];

const LIABILITY_LIMITS_FLORIDA = [
  "None",
  "No BI/10000",
  "10000/20000/10000",
  "15000/30000/10000",
  "25000/50000/25000",
  "50000/100000/25000",
  "50000/100000/50000",
  "100000/300000/50000",
  "100000/300000/100000",
  "250000/500000/100000",
  "250000/500000/250000",
  "300000/500000/250000",
  "300000/500000/300000",
  "500000/500000/300000",
  "500000/500000/500000",
  "500000/1000000/300000",
  "500000/1000000/500000",
  "1000000/1000000/500000",
  "1000000/1000000/1000000",
  "5000000/5000000/1000000",
  "100000 CSL",
  "300000 CSL",
  "500000 CSL",
];

const LIABILITY_LIMITS_DEFAULT = [
  "None",
  "25000/50000/25000",
  "50000/100000/25000",
  "50000/100000/50000",
  "100000/300000/50000",
  "100000/300000/100000",
  "250000/500000/100000",
  "250000/500000/250000",
  "300000/500000/250000",
  "300000/500000/300000",
  "500000/500000/300000",
  "500000/500000/500000",
  "500000/1000000/300000",
  "500000/1000000/500000",
  "1000000/1000000/500000",
  "1000000/1000000/1000000",
  "5000000/5000000/1000000",
  "100000 CSL",
  "300000 CSL",
  "500000 CSL",
];

const POLICY_TYPE_GEORGIA = ["Standard", "Nonowner", "Broadform"];

const REASON_FOR_NO_INSURANCE_FLORIDA = [
  "No Reason Given",
  "Car In Storage",
  "Company Car",
  "Deployed Military",
  "Did Not Own Vehicle",
  "Driving Without Insurance",
  "Incarcerated",
  "No Insurance Required",
  "Out Of Country",
  "Physically Impaired",
  "Self Insured",
  "Under Others Policy",
  "Other",
];
const REASON_FOR_NO_INSURANCE_DEFAULT = [
  "No Reason Given",
  "Car In Storage",
  "Company Car",
  "Deployed Military",
  "Did Not Own Vehicle",
  "Driving Without Insurance",
  "Incarcerated",
  "No Insurance Required",
  "Out Of Country",
  "Physically Impaired",
  "Self Insured",
  "Under Others Policy",
  "Other",
];

interface ContactInformationProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  workflowData?: any;
  currentStep?: number;
  totalSteps?: number;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  showChangeClient?: boolean;
  selectedState?: string;
  loading?: boolean;
}

interface ContactData {
  policyType?: string;
  priorInsurance?: string;
  timeWithPriorInsuranceYear?: string;
  timeWithPriorInsuranceMonth?: string;
  priorExpirationDate?: string;
  priorInsuranceCarrier?: string;
  priorCarrierId?: string;
  priorLiabilityLimits?: string;
  priorAddress?: string;
  priorCity?: string;
  priorState?: string;
  priorZipCode?: string;
  residenceType?: string;
  residenceStatus?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  cellphone: string;
  homePhone?: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
  yearsAtResidence?: string | number;
  monthsAtResidence?: string | number;
  serviceMessageAgreed?: boolean;
  optInEmail?: string;
  optInPhone?: string;
  optInText?: string;
  emailOptInStatus?: string;
  phoneOptInStatus?: string;
  textOptInStatus?: string;
  // State-specific Residence Information fields
  // identifier?: string;
  propertyInsurance?: string;
  customerDeclinedCredit?: string;
  // State-specific Prior Insurance History fields
  street2?: string;
  reasonForNoInsurance?: string;
  customerHasPriorWithThisAgency?: string;
}

export default function ContactInformation({
  onNext,
  onPrevious,
  workflowData,
  currentStep,
  totalSteps,
  isFirstStep,
  isLastStep,
  showChangeClient = false,
  selectedState,
  loading,
}: ContactInformationProps = {}) {
  const topContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const layoutContent = document.querySelector(".layout-content");
    if (layoutContent) {
      layoutContent.scrollTo({ top: 0, behavior: "auto" });
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const addressInputRef = useRef<HTMLInputElement>(null);
  const priorAddressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const priorAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const addressPlaceSelectedRef = useRef(false);
  const [contactLoading, setContactLoading] = useState(false);
  const toast = useRef<Toast | null>(null);

  const [contactData, setContactData] = useState<ContactData>({
    policyType: workflowData?.step1?.policyType || "",
    firstName: workflowData?.step1?.firstName || "",
    lastName: workflowData?.step1?.lastName || "",
    dateOfBirth: workflowData?.step1?.dateOfBirth ? formatDate(workflowData.step1.dateOfBirth) : "",
    cellphone: formatPhoneNumberDash(workflowData?.step1?.cellphone || ""),
    homePhone: formatPhoneNumberDash(workflowData?.step1?.homePhone || ""),
    email: workflowData?.step1?.email || "",
    address: workflowData?.step1?.address || "",
    city: workflowData?.step1?.city || "",
    state: workflowData?.step1?.state || selectedState || "",
    zipCode: workflowData?.step1?.zipCode || "",
    county: workflowData?.step1?.county || "",

    yearsAtResidence: workflowData?.step1?.yearsAtResidence || undefined,
    monthsAtResidence: workflowData?.step1?.monthsAtResidence || undefined,
    priorAddress: workflowData?.step1?.priorAddress || "",
    priorCity: workflowData?.step1?.priorCity || "",
    priorState: workflowData?.step1?.priorState || "",
    priorZipCode: workflowData?.step1?.priorZipCode || "",
    residenceType: workflowData?.step1?.residenceType || "",
    residenceStatus: workflowData?.step1?.residenceStatus || "",
    priorInsurance: workflowData?.step1?.priorInsurance || "",
    timeWithPriorInsuranceYear: workflowData?.step1?.timeWithPriorInsuranceYear || "",
    timeWithPriorInsuranceMonth: workflowData?.step1?.timeWithPriorInsuranceMonth || "",
    priorExpirationDate: workflowData?.step1?.priorExpirationDate || "",
    priorInsuranceCarrier: workflowData?.step1?.priorInsuranceCarrier || "",
    priorCarrierId: workflowData?.step1?.priorCarrierId || "",
    priorLiabilityLimits: workflowData?.step1?.priorLiabilityLimits || "",
    serviceMessageAgreed: workflowData?.step1?.serviceMessageAgreed || false,
    optInEmail: workflowData?.step1?.optInEmail || "",
    optInPhone: formatPhoneNumberDash(workflowData?.step1?.optInPhone || ""),
    optInText: workflowData?.step1?.optInText || "",
    emailOptInStatus: workflowData?.step1?.emailOptInStatus || "",
    phoneOptInStatus: workflowData?.step1?.phoneOptInStatus || "",
    textOptInStatus: workflowData?.step1?.textOptInStatus || "",
    // State-specific Residence Information fields
    // identifier: workflowData?.step1?.identifier || "",
    propertyInsurance: workflowData?.step1?.propertyInsurance || "",
    customerDeclinedCredit: workflowData?.step1?.customerDeclinedCredit || "",
    // State-specific Prior Insurance History fields
    street2: workflowData?.step1?.street2 || "",
    reasonForNoInsurance: workflowData?.step1?.reasonForNoInsurance || "",
    customerHasPriorWithThisAgency: workflowData?.step1?.customerHasPriorWithThisAgency || "",
  });

  // Service Messages agreement state
  const [serviceMessageAgreed, setServiceMessageAgreed] = useState(
    workflowData?.step1?.serviceMessageAgreed !== undefined ? workflowData.step1.serviceMessageAgreed : true,
  );

  // Opt-In/Opt-Out state for each communication type (only one can be opt-in at a time)
  const [optInStatus, setOptInStatus] = useState<{ email: string; phone: string; text: string }>({
    email: workflowData?.step1?.emailOptInStatus || "",
    phone: workflowData?.step1?.phoneOptInStatus || "",
    text: workflowData?.step1?.textOptInStatus || "",
  });
  const [optInEmail, setOptInEmail] = useState(workflowData?.step1?.optInEmail || "");
  const [optInPhone, setOptInPhone] = useState(formatPhoneNumberDash(workflowData?.step1?.optInPhone || ""));
  const [optInText, setOptInText] = useState(workflowData?.step1?.optInText || "");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [configurationLoaded, setConfigurationLoaded] = useState(false);
  const [previousSelectedState, setPreviousSelectedState] = useState<string>("");
  const [priorCarriers, setPriorCarriers] = useState<Array<{ text: string; values: string }>>(
    PRIOR_CARRIERS.map((name) => ({ text: name, values: "" })),
  );
  const [loadingCarriers, setLoadingCarriers] = useState(false);

  // Determine which liability limits to use based on state
  const liabilityLimits = contactData.state === "Florida" ? LIABILITY_LIMITS_FLORIDA : LIABILITY_LIMITS_DEFAULT;

  // const isWorkflowEmpty = !workflowData || Object.keys(workflowData).length === 0;
  useEffect(() => {
    const stateValue = workflowData?.step1?.state || contactData?.state;
    const hasExistingData = workflowData?.step1?.residenceType || workflowData?.step1?.priorInsurance;

    // Only call API if this is a new quote, state exists, configuration hasn't been loaded, and data isn't already populated
    if (workflowData?.isNewQuote && stateValue && !configurationLoaded && !hasExistingData) {
      getContactConfigurationDetails(stateValue);
      return;
    }

    // Only call for selectedState if it wasn't already loaded from workflowData
    if (selectedState && !workflowData?.step1?.state && !configurationLoaded && !hasExistingData) {
      getContactConfigurationDetails(selectedState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Update state when workflowData changes - MERGE with existing data, don't overwrite
  useEffect(() => {
    if (workflowData?.step1) {
      setContactData((prev) => ({
        ...prev, // Keep existing data (like configuration data)
        // Only update fields that have actual values in step1
        policyType: workflowData.step1.policyType || prev.policyType,
        firstName: workflowData.step1.firstName || prev.firstName,
        lastName: workflowData.step1.lastName || prev.lastName,
        dateOfBirth: workflowData.step1.dateOfBirth ? formatDate(workflowData.step1.dateOfBirth) : prev.dateOfBirth,
        cellphone: workflowData.step1.cellphone ? formatPhoneNumberDash(workflowData.step1.cellphone) : prev.cellphone,
        homePhone: workflowData.step1.homePhone ? formatPhoneNumberDash(workflowData.step1.homePhone) : prev.homePhone,
        email: workflowData.step1.email || prev.email,
        address: workflowData.step1.address || prev.address,
        city: workflowData.step1.city || prev.city,
        state: workflowData.step1.state || prev.state,
        zipCode: workflowData.step1.zipCode || prev.zipCode,
        county: workflowData.step1.county || prev.county,
        yearsAtResidence: workflowData.step1.yearsAtResidence !== undefined ? workflowData.step1.yearsAtResidence : prev.yearsAtResidence,
        monthsAtResidence: workflowData.step1.monthsAtResidence !== undefined ? workflowData.step1.monthsAtResidence : prev.monthsAtResidence,
        priorAddress: workflowData.step1.priorAddress || prev.priorAddress,
        priorCity: workflowData.step1.priorCity || prev.priorCity,
        priorState: workflowData.step1.priorState || prev.priorState,
        priorZipCode: workflowData.step1.priorZipCode || prev.priorZipCode,
        residenceType: workflowData.step1.residenceType || prev.residenceType,
        residenceStatus: workflowData.step1.residenceStatus || prev.residenceStatus,
        priorInsurance: workflowData.step1.priorInsurance || prev.priorInsurance,
        timeWithPriorInsuranceYear: workflowData.step1.timeWithPriorInsuranceYear || prev.timeWithPriorInsuranceYear,
        timeWithPriorInsuranceMonth: workflowData.step1.timeWithPriorInsuranceMonth || prev.timeWithPriorInsuranceMonth,
        priorExpirationDate: workflowData.step1.priorExpirationDate || prev.priorExpirationDate,
        priorInsuranceCarrier: workflowData.step1.priorInsuranceCarrier || prev.priorInsuranceCarrier,
        priorCarrierId: workflowData.step1.priorCarrierId || prev.priorCarrierId,
        priorLiabilityLimits: workflowData.step1.priorLiabilityLimits || prev.priorLiabilityLimits,
        serviceMessageAgreed: workflowData.step1.serviceMessageAgreed ?? prev.serviceMessageAgreed,
        optInEmail: workflowData.step1.optInEmail || prev.optInEmail,
        optInPhone: workflowData.step1.optInPhone ? formatPhoneNumberDash(workflowData.step1.optInPhone) : prev.optInPhone,
        optInText: workflowData.step1.optInText || prev.optInText,
        emailOptInStatus: workflowData.step1.emailOptInStatus || prev.emailOptInStatus,
        phoneOptInStatus: workflowData.step1.phoneOptInStatus || prev.phoneOptInStatus,
        textOptInStatus: workflowData.step1.textOptInStatus || prev.textOptInStatus,
        // identifier: workflowData?.step1?.identifier || prev.identifier,
        propertyInsurance: workflowData?.step1?.propertyInsurance || prev.propertyInsurance,
        customerDeclinedCredit: workflowData?.step1?.customerDeclinedCredit || prev.customerDeclinedCredit,
        street2: workflowData?.step1?.street2 || prev.street2,
        reasonForNoInsurance: workflowData?.step1?.reasonForNoInsurance || prev.reasonForNoInsurance,
        customerHasPriorWithThisAgency: workflowData?.step1?.customerHasPriorWithThisAgency || prev.customerHasPriorWithThisAgency,
      }));

      setServiceMessageAgreed(workflowData.step1.serviceMessageAgreed ?? true);
      setOptInStatus({
        email: workflowData.step1.emailOptInStatus || "",
        phone: workflowData.step1.phoneOptInStatus || "",
        text: workflowData.step1.textOptInStatus || "",
      });
      setOptInEmail(workflowData.step1.optInEmail || "");
      setOptInPhone(formatPhoneNumberDash(workflowData.step1.optInPhone || ""));
      setOptInText(workflowData.step1.optInText || "");
    }
  }, [workflowData]);

  // Load Google Maps script and initialize autocomplete
  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }

      if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          initAutocomplete();
        };
        document.head.appendChild(script);
      }
    };

    const initAutocomplete = () => {
      if (!addressInputRef.current || autocompleteRef.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ["address"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        addressPlaceSelectedRef.current = true;

        if (place.address_components) {
          let streetNumber = "";
          let route = "";
          let city = "";
          let state = "";
          let zipCode = "";
          let county = "";

          place.address_components.forEach((component: any) => {
            const types = component.types;
            if (types.includes("street_number")) {
              streetNumber = component.long_name;
            }
            if (types.includes("route")) {
              route = component.long_name;
            }
            if (types.includes("locality")) {
              city = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              state = component.short_name;
            }
            if (types.includes("postal_code")) {
              zipCode = component.long_name;
            }
            if (types.includes("administrative_area_level_2")) {
              county = component.long_name;
            }
          });

          const fullAddress = `${streetNumber} ${route}`.trim();
          const fullStateName = state ? stateAbbreviationMap[state] || state : "";
          setContactData((prev) => ({
            ...prev,
            address: fullAddress || prev.address,
            city: city || prev.city,
            state: fullStateName || prev.state,
            zipCode: zipCode || prev.zipCode,
            county: county || prev.county,
          }));
        }
      });

      autocompleteRef.current = autocomplete;
    };

    loadGoogleMaps();
  }, [workflowData]);

  const geocodeAddress = (addressValue: string) => {
    if (!window.google || !window.google.maps || !addressValue.trim()) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addressValue }, (results: any, status: any) => {
      if (status === "OK" && results && results[0]) {
        const components = results[0].address_components;
        let streetNumber = "";
        let route = "";
        let city = "";
        let state = "";
        let zipCode = "";
        let county = "";
        components.forEach((component: any) => {
          const types = component.types;
          if (types.includes("street_number")) streetNumber = component.long_name;
          if (types.includes("route")) route = component.long_name;
          if (types.includes("locality")) city = component.long_name;
          if (types.includes("administrative_area_level_1")) state = component.short_name;
          if (types.includes("postal_code")) zipCode = component.long_name;
          if (types.includes("administrative_area_level_2")) county = component.long_name;
        });
        const fullAddress = `${streetNumber} ${route}`.trim();
        const fullStateName = state ? stateAbbreviationMap[state] || state : "";
        setContactData((prev) => ({
          ...prev,
          address: fullAddress || prev.address,
          city: city || prev.city,
          state: fullStateName || prev.state,
          zipCode: zipCode || prev.zipCode,
          county: county || prev.county,
        }));
      }
    });
  };

  // Initialize autocomplete for prior address when it becomes visible
  useEffect(() => {
    const initPriorAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        return;
      }

      if (priorAddressInputRef.current && !priorAutocompleteRef.current) {
        const priorAutocomplete = new window.google.maps.places.Autocomplete(priorAddressInputRef.current, {
          types: ["address"],
        });

        priorAutocomplete.addListener("place_changed", () => {
          const place = priorAutocomplete.getPlace();

          if (place.address_components) {
            let streetNumber = "";
            let route = "";
            let city = "";
            let state = "";
            let zipCode = "";

            place.address_components.forEach((component: any) => {
              const types = component.types;
              if (types.includes("street_number")) {
                streetNumber = component.long_name;
              }
              if (types.includes("route")) {
                route = component.long_name;
              }
              if (types.includes("locality")) {
                city = component.long_name;
              }
              if (types.includes("administrative_area_level_1")) {
                state = component.short_name;
              }
              if (types.includes("postal_code")) {
                zipCode = component.long_name;
              }
            });

            const fullAddress = `${streetNumber} ${route}`.trim();
            const fullStateName = state ? stateAbbreviationMap[state] || state : "";

            setContactData((prev) => ({
              ...prev,
              priorAddress: fullAddress || prev.priorAddress,
              priorCity: city || prev.priorCity,
              priorState: fullStateName || prev.priorState,
              priorZipCode: zipCode || prev.priorZipCode,
            }));
          }
        });

        priorAutocompleteRef.current = priorAutocomplete;
      }
    };

    // Only initialize when prior address section is visible (yearsAtResidence < 3)
    if (Number(contactData.yearsAtResidence) < 3) {
      initPriorAutocomplete();
    }
  }, [contactData.yearsAtResidence]);

  // Watch for state changes and call configuration API
  useEffect(() => {
    // Skip if no state
    if (!contactData.state) return;

    // If state changed from a previous value, call the API
    if (previousSelectedState && previousSelectedState !== contactData.state) {
      getContactConfigurationDetails(contactData.state);
      setPreviousSelectedState(contactData.state);
    } else if (!previousSelectedState && contactData.state) {
      // Set initial previous state and call API if this is not from initial workflowData
      setPreviousSelectedState(contactData.state);

      // Only call API if this state wasn't loaded from workflowData/selectedState initially
      const isInitialState = contactData.state === (workflowData?.step1?.state || selectedState);
      if (!isInitialState) {
        getContactConfigurationDetails(contactData.state);
      }
    }
  }, [contactData.state]);

  /** Fetch Prior Carriers dynamically based on state */
  useEffect(() => {
    const fetchPriorCarriers = async () => {
      if (!contactData.state || contactData.state.trim() === "") {
        setPriorCarriers(PRIOR_CARRIERS.map((name) => ({ text: name, values: "" }))); // Use default if no state
        return;
      }

      const stateAbbr = STATE_ABBREVIATIONS[contactData.state];
      if (!stateAbbr) {
        setPriorCarriers(PRIOR_CARRIERS.map((name) => ({ text: name, values: "" }))); // Use default if state not found
        return;
      }

      setLoadingCarriers(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_ACE_API_Url}/api/rate/dropdown/values`, {
          params: {
            state: stateAbbr,
            requestedValue: "Prior_Carriers",
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        });
        if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          // Preserve both text and values from API response
          const carriers = response.data.data.map((item: any) => ({
            text: item.text || "",
            values: item.values || "",
          }));
          setPriorCarriers(carriers);
        } else {
          setPriorCarriers(PRIOR_CARRIERS.map((name) => ({ text: name, values: "" }))); // Fallback to default
        }
      } catch (error) {
        console.error("Error fetching prior carriers:", error);
        setPriorCarriers(PRIOR_CARRIERS.map((name) => ({ text: name, values: "" }))); // Fallback to default on error
      } finally {
        setLoadingCarriers(false);
      }
    };

    fetchPriorCarriers();
  }, [contactData.state]);

  // When priorCarriers load or priorInsuranceCarrier changes, look up and set the carrier ID
  useEffect(() => {
    if (contactData.priorInsuranceCarrier && priorCarriers.length > 0 && !contactData.priorCarrierId) {
      const matchedCarrier = priorCarriers.find((c) => c.text === contactData.priorInsuranceCarrier);
      if (matchedCarrier?.values) {
        setContactData((prev) => ({
          ...prev,
          priorCarrierId: matchedCarrier.values,
        }));
      }
    }
  }, [priorCarriers, contactData.priorInsuranceCarrier]);

  // Load configuration details only on initial mount if state is already set and data not already populated

  const getContactConfigurationDetails = (state: any) => {
    setContactLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/contactinformationconfiguration?state=${state}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
    })
      .then((response: any) => {
        const data = response.data;
        setContactData((prev) => ({
          ...prev,
          policyType: data.policyType || prev.policyType || "",
          residenceType: data.residentType ?? "",
          residenceStatus: data.residenceStatus ?? "",

          yearsAtResidence: data.timeAtResidenceYears ?? "",
          monthsAtResidence: data.timeAtResidenceMonths ?? "",

          priorInsurance: data.priorInsurance ?? "",
          timeWithPriorInsuranceYear: data.priorInsuranceTimeYears ?? "",
          timeWithPriorInsuranceMonth: data.priorInsuranceTimeMonths ?? "",

          priorInsuranceCarrier: data.priorInsuranceCarrier ?? "",
          priorLiabilityLimits: data.priorLiabilityLimits ?? "",
          customerDeclinedCredit: data.customerDeclinedCredit ?? "",
          propertyInsurance: data.propertyInsurance ?? "",
          reasonForNoInsurance: data.reasonForNoInsurance ?? "",
        }));
        setConfigurationLoaded(true);
        setContactLoading(false);
      })
      .catch((error: any) => {
        setContactLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while searching the client!"}`
                : "Unknown error while getting  the contact configuration details!",
            life: 3000,
          });
        }
      });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Policy Type
    if (!contactData.policyType) errors.policyType = "Policy type is required";

    // Personal Information
    if (!contactData.firstName?.trim()) errors.firstName = "First name is required";
    if (!contactData.lastName?.trim()) errors.lastName = "Last name is required";
    if (!contactData.dateOfBirth) errors.dateOfBirth = "Date of birth is required";
    if (!contactData.email?.trim()) errors.email = "Email is required";
    // if (!contactData.cellphone?.trim()) errors.cellphone = "Cell phone is required";
    if (!contactData.customerDeclinedCredit) errors.customerDeclinedCredit = "Customer declined credit selection is required";

    // Address
    if (!contactData.address?.trim()) errors.address = "Street address is required";
    if (!contactData.city?.trim()) errors.city = "City is required";
    if (!contactData.state) errors.state = "State is required";
    if (!contactData.zipCode?.trim()) errors.zipCode = "ZIP code is required";
    // if (!contactData.county?.trim()) errors.county = "County is required";

    // Residence Information
    if (!contactData.residenceType) errors.residenceType = "Residence type is required";
    if (!contactData.residenceStatus) errors.residenceStatus = "Residence status is required";
    // if (contactData.yearsAtResidence === undefined || contactData.yearsAtResidence === "") {
    //   errors.yearsAtResidence = "Years at residence is required";
    // }

    // Prior Address (if years at residence < 3)
    if (Number(contactData.yearsAtResidence) < 3 && contactData.yearsAtResidence !== "") {
      if (!contactData.priorAddress?.trim()) errors.priorAddress = "Prior street address is required";
      if (!contactData.priorCity?.trim()) errors.priorCity = "Prior city is required";
      if (!contactData.priorState?.trim()) errors.priorState = "Prior state is required";
      if (!contactData.priorZipCode?.trim()) errors.priorZipCode = "Prior ZIP code is required";
    }

    // Prior Insurance
    if (!contactData.priorInsurance) errors.priorInsurance = "Prior insurance selection is required";

    // Only validate other prior insurance fields if Prior Insurance is Yes
    if (contactData.priorInsurance === "Yes") {
      if (!contactData.timeWithPriorInsuranceMonth) errors.timeWithPriorInsuranceMonth = "Time with prior insurance (months) is required";
      if (!contactData.priorExpirationDate) errors.priorExpirationDate = "Prior expiration date is required";
      if (!contactData.priorInsuranceCarrier) errors.priorInsuranceCarrier = "Prior insurance carrier is required";
      if (!contactData.priorLiabilityLimits) errors.priorLiabilityLimits = "Prior liability limits is required";
    }

    // Validate Reason for No Insurance if Prior Insurance is No
    if (contactData.priorInsurance === "No") {
      if (!contactData.reasonForNoInsurance) errors.reasonForNoInsurance = "Reason for no insurance is required";
    }

    // Communication Preferences: Each must be selected (opt-in or opt-out)
    if (!optInStatus.email) {
      errors.emailOptInStatus = "Please select opt-in or opt-out for Email";
    }
    if (!optInStatus.phone) {
      errors.phoneOptInStatus = "Please select opt-in or opt-out for Phone";
    }
    if (!optInStatus.text) {
      errors.textOptInStatus = "Please select opt-in or opt-out for Text";
    }

    // Validate opt-in email/phone/text if selected
    if (optInStatus.email === "opt-in" && !optInEmail?.trim()) {
      errors.optInEmail = "Email is required when opting in";
    }
    if (optInStatus.phone === "opt-in" && !optInPhone?.trim()) {
      errors.optInPhone = "Phone is required when opting in";
    }
    if (optInStatus.text === "opt-in" && !optInText?.trim()) {
      errors.optInText = "Text number is required when opting in";
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all required fields",
        life: 3000,
      });
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`) || document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return false;
    }

    return true;
  };

  const handleInputChange = (field: keyof ContactData, value: string | number | undefined) => {
    // Track previous state when state field changes and call API only once

    if (field === "state" && value) {
      if (contactData.state && contactData.state !== value) {
        setPreviousSelectedState(contactData.state);
      }
      getContactConfigurationDetails(value as string);
    }

    // Clear validation error for this field when user types
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Clear prior address errors when yearsAtResidence changes to >= 3
    if (field === "yearsAtResidence" && Number(value) >= 3) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.priorAddress;
        delete newErrors.priorCity;
        delete newErrors.priorState;
        delete newErrors.priorZipCode;
        return newErrors;
      });
    }

    const formattedValue = (field === "cellphone" || field === "homePhone") && typeof value === "string"
      ? formatPhoneNumberDash(value)
      : value;

    setContactData((prev) => ({
      ...prev,
      [field]: formattedValue,
    }));
  };

  // Handle opt-in/opt-out changes - allow independent selection for each type
  const handleOptInChange = (type: "email" | "phone" | "text", status: string) => {
    // Clear validation error for this type
    const errorKey = type + "OptInStatus";
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
    setOptInStatus((prev) => ({
      ...prev,
      [type]: status,
    }));

    // Auto-populate opt-in fields for email and phone only
    if (status === "opt-in") {
      if (type === "email" && contactData.email && !optInEmail) {
        setOptInEmail(contactData.email);
      } else if (type === "phone" && contactData.cellphone && !optInPhone) {
        setOptInPhone(contactData.cellphone);
      } else if (type === "text" && contactData.cellphone && !optInText) {
        setOptInText(contactData.cellphone);
      }
      // Do NOT auto-populate for text
    }
  };

  const handleChangeClient = () => {
    navigate("/search-client");
  };

  const handleClearForm = () => {
    setContactData({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      cellphone: "",
      homePhone: "",
      email: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      county: "United States",
    });
  };

  // Check if we came from review page via navigation state
  const isFromReview = location.state?.fromReview === true;

  const handlePreviousFromReview = () => {
    navigate("/review");
  };

  const handleContinueFromReview = () => {
    // Save the data and return to review
    navigate("/review");
  };

  const content = (
    <div className="page-container" ref={topContainerRef}>
      <Toast ref={toast} />

      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title-section">
            <h1>Contact Information</h1>
            <span className="title-separator">|</span>
            <p>Enter and manage client contact details</p>
          </div>
        </div>
      </div>
      <div>
        {/* <nav className="quick-jump-sidebar">
          <div className="sidebar-nav-header">
            <h2 className="sidebar-nav-title">Quick Navigation</h2>
          </div>
          <ul className="sidebar-nav-list">
            <li>
              <button
                type="button"
                className="sidebar-nav-btn"
                onClick={() => document.getElementById("personal-info")?.scrollIntoView({ behavior: "smooth" })}
              >
                Personal Information
              </button>
            </li>
            <li>
              <button
                type="button"
                className="sidebar-nav-btn"
                onClick={() => document.getElementById("residence-info")?.scrollIntoView({ behavior: "smooth" })}
              >
                Residence Information
              </button>
            </li>
            <li>
              <button
                type="button"
                className="sidebar-nav-btn"
                onClick={() => document.getElementById("prior-insurance")?.scrollIntoView({ behavior: "smooth" })}
              >
                Prior Insurance History
              </button>
            </li>
            <li>
              <button
                type="button"
                className="sidebar-nav-btn"
                onClick={() => document.getElementById("communication-preferences")?.scrollIntoView({ behavior: "smooth" })}
              >
                Communication Preferences
              </button>
            </li>
          </ul>
        </nav> */}
        <div id="personal-info" className="form-grid">
          {/* Policy Type Section */}
          <div className="form-section-header form-group full-width">
            <h3>Policy Type</h3>
          </div>
          <div className="form-group">
            <label>
              Policy Type <span className="text-danger">*</span>
            </label>
            <select
              name="policyType"
              className={`form-input compact-select ${validationErrors.policyType ? "error" : ""}`}
              value={contactData.policyType}
              onChange={(e) => handleInputChange("policyType", e.target.value)}
            >
              <option value="">Select policy type</option>
              <option value="Standard">Standard</option>
              <option value="Nonowner">Nonowner</option>
              {contactData.state !== "Florida" && <option value="Broadform">Broadform</option>}
            </select>
            {validationErrors.policyType && <span className="error-message">{validationErrors.policyType}</span>}
          </div>
          {/* Personal Information Section */}
          <div className="form-section-header form-group full-width">
            <h3>Personal Information</h3>
          </div>
          <div className="form-group">
            <label>
              First Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              className={`form-input ${validationErrors.firstName ? "error" : ""}`}
              placeholder="Enter first name"
              value={contactData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
            />
            {validationErrors.firstName && <span className="error-message">{validationErrors.firstName}</span>}
          </div>
          <div className="form-group">
            <label>
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              className={`form-input ${validationErrors.lastName ? "error" : ""}`}
              placeholder="Enter last name"
              value={contactData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
            />
            {validationErrors.lastName && <span className="error-message">{validationErrors.lastName}</span>}
          </div>
          <div className="form-group">
            <label>
              Date of Birth <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              placeholder="MM/DD/YYYY"
              className={`form-input ${validationErrors.dateOfBirth ? "error" : ""}`}
              value={contactData.dateOfBirth}
              onChange={(e) => handleInputChange("dateOfBirth", formatDateOfBirth(e.target.value))}
              autoComplete="off"
            />
            {validationErrors.dateOfBirth && <span className="error-message">{validationErrors.dateOfBirth}</span>}
          </div>
          <div className="form-group">
            <label>Cell Phone</label>
            <input
              type="tel"
              name="cellphone"
              className={`form-input ${validationErrors.cellphone ? "error" : ""}`}
              placeholder="Enter cell phone number"
              value={contactData.cellphone}
              onChange={(e) => handleInputChange("cellphone", e.target.value)}
            />
            {validationErrors.cellphone && <span className="error-message">{validationErrors.cellphone}</span>}
          </div>
          <div className="form-group">
            <label>Home Phone</label>
            <input
              type="tel"
              className="form-input"
              placeholder="Enter home phone number"
              value={contactData.homePhone}
              onChange={(e) => handleInputChange("homePhone", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>
              Email <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              name="email"
              className={`form-input ${validationErrors.email ? "error" : ""}`}
              placeholder="Enter email address"
              value={contactData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
            {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
          </div>
          <div className="form-group">
            <label>
              Customer Declined Credit<span className="text-danger">*</span>
            </label>
            <select
              name="customerDeclinedCredit"
              className={`form-input compact-select ${validationErrors.customerDeclinedCredit ? "error" : ""}`}
              value={contactData.customerDeclinedCredit}
              onChange={(e) => handleInputChange("customerDeclinedCredit", e.target.value)}
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {validationErrors.customerDeclinedCredit && <span className="error-message">{validationErrors.customerDeclinedCredit}</span>}
          </div>
          {/* Address Section */}
          <div className="form-section-header form-group full-width">
            <h3>Address</h3>
          </div>
          <div className="form-group full-width">
            <label>
              Street Address <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="address"
              className={`form-input ${validationErrors.address ? "error" : ""}`}
              placeholder="Enter street address"
              ref={addressInputRef}
              value={contactData.address}
              onChange={(e) => { addressPlaceSelectedRef.current = false; handleInputChange("address", e.target.value); }}
              onBlur={(e) => { if (!addressPlaceSelectedRef.current) geocodeAddress(e.target.value); }}
            />
            {validationErrors.address && <span className="error-message">{validationErrors.address}</span>}
          </div>
          <div className="form-group full-width">
            <label>Address 2</label>
            <input
              type="text"
              name="street2"
              className="form-input"
              placeholder="Apartment, suite, unit, etc. (optional)"
              value={contactData.street2}
              onChange={(e) => handleInputChange("street2", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>
              City <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="city"
              className={`form-input ${validationErrors.city ? "error" : ""}`}
              placeholder="Enter city"
              value={contactData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
            />
            {validationErrors.city && <span className="error-message">{validationErrors.city}</span>}
          </div>
          <div className="form-group">
            <label>
              State <span className="text-danger">*</span>
            </label>
            <select
              name="state"
              className={`form-input compact-select ${validationErrors.state ? "error" : ""}`}
              value={contactData.state}
              onChange={(e) => handleInputChange("state", e.target.value)}
            >
              <option value="">Select state</option>
              <option value="Alabama">Alabama</option>
              <option value="Alaska">Alaska</option>
              <option value="Arizona">Arizona</option>
              <option value="Arkansas">Arkansas</option>
              <option value="California">California</option>
              <option value="Colorado">Colorado</option>
              <option value="Connecticut">Connecticut</option>
              <option value="Delaware">Delaware</option>
              <option value="Florida">Florida</option>
              <option value="Georgia">Georgia</option>
              <option value="Hawaii">Hawaii</option>
              <option value="Idaho">Idaho</option>
              <option value="Illinois">Illinois</option>
              <option value="Indiana">Indiana</option>
              <option value="Iowa">Iowa</option>
              <option value="Kansas">Kansas</option>
              <option value="Kentucky">Kentucky</option>
              <option value="Louisiana">Louisiana</option>
              <option value="Maine">Maine</option>
              <option value="Maryland">Maryland</option>
              <option value="Massachusetts">Massachusetts</option>
              <option value="Michigan">Michigan</option>
              <option value="Minnesota">Minnesota</option>
              <option value="Mississippi">Mississippi</option>
              <option value="Missouri">Missouri</option>
              <option value="Montana">Montana</option>
              <option value="Nebraska">Nebraska</option>
              <option value="Nevada">Nevada</option>
              <option value="New Hampshire">New Hampshire</option>
              <option value="New Jersey">New Jersey</option>
              <option value="New Mexico">New Mexico</option>
              <option value="New York">New York</option>
              <option value="North Carolina">North Carolina</option>
              <option value="North Dakota">North Dakota</option>
              <option value="Ohio">Ohio</option>
              <option value="Oklahoma">Oklahoma</option>
              <option value="Oregon">Oregon</option>
              <option value="Pennsylvania">Pennsylvania</option>
              <option value="Rhode Island">Rhode Island</option>
              <option value="South Carolina">South Carolina</option>
              <option value="South Dakota">South Dakota</option>
              <option value="Tennessee">Tennessee</option>
              <option value="Texas">Texas</option>
              <option value="Utah">Utah</option>
              <option value="Vermont">Vermont</option>
              <option value="Virginia">Virginia</option>
              <option value="Washington">Washington</option>
              <option value="West Virginia">West Virginia</option>
              <option value="Wisconsin">Wisconsin</option>
              <option value="Wyoming">Wyoming</option>
            </select>
            {validationErrors.state && <span className="error-message">{validationErrors.state}</span>}
          </div>
          <div className="form-group">
            <label>
              ZIP Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="zipCode"
              className={`form-input ${validationErrors.zipCode ? "error" : ""}`}
              placeholder="Enter ZIP code"
              maxLength={10}
              value={contactData.zipCode}
              onChange={(e) => handleInputChange("zipCode", e.target.value)}
            />
            {validationErrors.zipCode && <span className="error-message">{validationErrors.zipCode}</span>}
          </div>
          <div className="form-group">
            <label>County</label>
            <input
              type="text"
              name="county"
              className={`form-input ${validationErrors.county ? "error" : ""}`}
              value={contactData.county}
              placeholder="Enter county"
              onChange={(e) => handleInputChange("county", e.target.value)}
            />
            {validationErrors.county && <span className="error-message">{validationErrors.county}</span>}
          </div>

          {/* Residence Information Section */}
          <div id="residence-info" className="form-section-header form-group full-width">
            <h3>Residence Information</h3>
          </div>
          {/* State-agnostic fields */}
          <div className="form-group">
            <label>
              Residence Type <span className="text-danger">*</span>
            </label>
            <select
              name="residenceType"
              className={`form-input compact-select ${validationErrors.residenceType ? "error" : ""}`}
              value={contactData.residenceType}
              onChange={(e) => handleInputChange("residenceType", e.target.value)}
            >
              <option value="">Select residence type</option>
              <option value="Home">Home</option>
              <option value="Apartment">Apartment</option>
              <option value="Condo">Condo</option>
              <option value="Mobile Home">Mobile Home</option>
              <option value="Fixed Mobile Home">Fixed Mobile Home</option>
            </select>
            {validationErrors.residenceType && <span className="error-message">{validationErrors.residenceType}</span>}
          </div>
          <div className="form-group">
            <label>
              Residence Status <span className="text-danger">*</span>
            </label>
            <select
              name="residenceStatus"
              className={`form-input compact-select ${validationErrors.residenceStatus ? "error" : ""}`}
              value={contactData.residenceStatus}
              onChange={(e) => handleInputChange("residenceStatus", e.target.value)}
            >
              <option value="">Select residence status</option>
              <option value="Own">Own</option>
              <option value="Rent">Rent</option>
              <option value="Lease">Lease</option>
            </select>
            {validationErrors.residenceStatus && <span className="error-message">{validationErrors.residenceStatus}</span>}
          </div>
          <div className="form-group">
            <label>Time at Residence</label>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span>Years</span>
              <input
                type="number"
                name="yearsAtResidence"
                className={`form-input ${validationErrors.yearsAtResidence ? " error" : ""}`}
                style={{ width: "auto" }}
                value={contactData.yearsAtResidence !== undefined ? String(contactData.yearsAtResidence) : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || (Number(value) >= 0 && Number(value) <= 99)) {
                    handleInputChange("yearsAtResidence", value);
                  }
                }}
                min={0}
                max={99}
                step={1}
                placeholder="0–99"
                onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              />

              <input
                type="number"
                name="monthsAtResidence"
                className="form-input"
                style={{ width: "auto" }}
                value={contactData.monthsAtResidence !== undefined ? String(contactData.monthsAtResidence) : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || (Number(value) >= 0 && Number(value) <= 11)) {
                    handleInputChange("monthsAtResidence", value);
                  }
                }}
                min={0}
                max={11}
                step={1}
                placeholder="0–11"
                onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              />
              <span>
                Months<span className="text-danger">*</span>
              </span>
            </div>
            {validationErrors.yearsAtResidence && <span className="error-message">{validationErrors.yearsAtResidence}</span>}
          </div>
          <PropertyInsuranceField
            value={contactData.propertyInsurance ?? ""}
            onChange={(e: any) => handleInputChange("propertyInsurance", e.target.value)}
          />

          {/* State-specific Residence Information fields */}

          {/* Prior Address Section */}
          {Number(contactData.yearsAtResidence) < 3 && contactData.yearsAtResidence !== "" && (
            <>
              <div className="form-section-header form-group full-width">
                <h3>Prior Address</h3>
              </div>
              <div className="form-group full-width">
                <label>
                  Prior Street Address <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="priorAddress"
                  className={`form-input ${validationErrors.priorAddress ? "error" : ""}`}
                  placeholder="Enter prior street address"
                  ref={priorAddressInputRef}
                  value={contactData.priorAddress}
                  onChange={(e) => handleInputChange("priorAddress", e.target.value)}
                />
                {validationErrors.priorAddress && <span className="error-message">{validationErrors.priorAddress}</span>}
              </div>
              <div className="form-group">
                <label>
                  Prior City <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="priorCity"
                  className={`form-input ${validationErrors.priorCity ? "error" : ""}`}
                  placeholder="Enter prior city"
                  value={contactData.priorCity}
                  onChange={(e) => handleInputChange("priorCity", e.target.value)}
                />
                {validationErrors.priorCity && <span className="error-message">{validationErrors.priorCity}</span>}
              </div>
              <div className="form-group">
                <label>
                  Prior State <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="priorState"
                  className={`form-input ${validationErrors.priorState ? "error" : ""}`}
                  placeholder="Enter prior state"
                  value={contactData.priorState}
                  onChange={(e) => handleInputChange("priorState", e.target.value)}
                />
                {validationErrors.priorState && <span className="error-message">{validationErrors.priorState}</span>}
              </div>
              <div className="form-group">
                <label>
                  Prior ZIP Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="priorZipCode"
                  className={`form-input ${validationErrors.priorZipCode ? "error" : ""}`}
                  placeholder="Enter prior ZIP code"
                  value={contactData.priorZipCode}
                  onChange={(e) => handleInputChange("priorZipCode", e.target.value)}
                />
                {validationErrors.priorZipCode && <span className="error-message">{validationErrors.priorZipCode}</span>}
              </div>
            </>
          )}

          {/* Prior Insurance History Section */}
          <div id="prior-insurance" className="form-section-header form-group full-width">
            <h3>Prior Insurance History</h3>
          </div>
          <div className="form-group">
            <label>
              Prior Insurance <span className="text-danger">*</span>
            </label>
            <select
              name="priorInsurance"
              className={`form-input compact-select ${validationErrors.priorInsurance ? "error" : ""}`}
              value={contactData.priorInsurance}
              onChange={(e) => handleInputChange("priorInsurance", e.target.value)}
            >
              <option value="">Select prior insurance status</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            {validationErrors.priorInsurance && <span className="error-message">{validationErrors.priorInsurance}</span>}
          </div>
          <div className="form-group">
            <label>
              Time with Prior Insurance Year
              {/* {contactData.priorInsurance === "Yes" && <span className="text-danger"> *</span>} */}
            </label>
            <input
              type="number"
              name="timeWithPriorInsuranceYear"
              className={`form-input ${validationErrors.timeWithPriorInsuranceYear ? "error" : ""}`}
              value={contactData.timeWithPriorInsuranceYear}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || (Number(val) >= 0 && Number(val) <= 99)) {
                  handleInputChange("timeWithPriorInsuranceYear", val);
                }
              }}
              min={0}
              max={99}
              step={1}
              placeholder="0–99"
              onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
            />
            {validationErrors.timeWithPriorInsuranceYear && <span className="error-message">{validationErrors.timeWithPriorInsuranceYear}</span>}
          </div>
          <div className="form-group">
            <label>
              Time with Prior Insurance Month
              {contactData.priorInsurance === "Yes" && <span className="text-danger"> *</span>}
            </label>
            <input
              type="number"
              name="timeWithPriorInsuranceMonth"
              className={`form-input ${validationErrors.timeWithPriorInsuranceMonth ? "error" : ""}`}
              placeholder="0–11"
              value={contactData.timeWithPriorInsuranceMonth}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleInputChange("timeWithPriorInsuranceMonth", "");
                  return;
                }
                const num = Number(val);
                if (num < 0 || num > 11) return;
                handleInputChange("timeWithPriorInsuranceMonth", val);
              }}
              min={0}
              max={11}
              step={1}
              onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
            />
            {validationErrors.timeWithPriorInsuranceMonth && <span className="error-message">{validationErrors.timeWithPriorInsuranceMonth}</span>}
          </div>
          <div className="form-group">
            <label>
              Prior Expiration Date
              {contactData.priorInsurance === "Yes" && <span className="text-danger"> *</span>}
            </label>
            <DatePicker
              selected={contactData.priorExpirationDate ? new Date(contactData.priorExpirationDate) : null}
              onChange={(date: Date | null) => handleInputChange("priorExpirationDate", date ? formatDate(date) : "")}
              dateFormat="MM/dd/yyyy"
              placeholderText="MM/DD/YYYY"
              className={`form-input ${validationErrors.priorExpirationDate ? "error" : ""}`}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={15}
              minDate={new Date()}
              autoComplete="off"
            />
            {validationErrors.priorExpirationDate && <span className="error-message">{validationErrors.priorExpirationDate}</span>}
          </div>

          <div className="form-group">
            <label>
              Prior Insurance Carrier
              {contactData.priorInsurance === "Yes" && <span className="text-danger"> *</span>}
            </label>

            <select
              name="priorInsuranceCarrier"
              className={`form-input compact-select ${validationErrors.priorInsuranceCarrier ? "error" : ""}`}
              value={contactData.priorInsuranceCarrier}
              onChange={(e) => {
                const selectedText = e.target.value;
                const selectedCarrier = priorCarriers.find((carrier) => carrier.text === selectedText);
                setContactData((prev) => ({
                  ...prev,
                  priorInsuranceCarrier: selectedText,
                  priorCarrierId: selectedCarrier?.values || "",
                }));
              }}
              disabled={loadingCarriers}
            >
              <option value="">{loadingCarriers ? "Loading carriers..." : "Select carrier"}</option>
              {priorCarriers.map((carrier) => (
                <option key={carrier.text} value={carrier.text}>
                  {carrier.text}
                </option>
              ))}
            </select>
            {validationErrors.priorInsuranceCarrier && <span className="error-message">{validationErrors.priorInsuranceCarrier}</span>}
          </div>
          <div className="form-group">
            <label>
              Prior Liability Limits
              {contactData.priorInsurance === "Yes" && <span className="text-danger"> *</span>}
            </label>
            <select
              className={`form-input compact-select ${validationErrors.priorLiabilityLimits ? "error" : ""}`}
              value={contactData.priorLiabilityLimits}
              onChange={(e) => handleInputChange("priorLiabilityLimits", e.target.value)}
            >
              <option value="">Select liability limits</option>
              {liabilityLimits.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {validationErrors.priorLiabilityLimits && <span className="error-message">{validationErrors.priorLiabilityLimits}</span>}
          </div>

          <div className="form-group">
            <label>
              Reason For No Insurance
              {contactData.priorInsurance === "No" && <span className="text-danger"> *</span>}
            </label>
            <ReasonForNoInsuranceDropdown
              value={contactData.reasonForNoInsurance ?? ""}
              onChange={(e: any) => handleInputChange("reasonForNoInsurance", e.target.value)}
              state={contactData.state}
              error={!!validationErrors.reasonForNoInsurance}
            />
            {validationErrors.reasonForNoInsurance && <span className="error-message">{validationErrors.reasonForNoInsurance}</span>}
          </div>
          {/* Email Communication Preference Section */}
          <div id="communication-preferences" className="form-section-header form-group full-width" style={{ marginTop: "16px" }}>
            <h3>Communication Preferences</h3>
          </div>
          <div className="form-group full-width">
            {/* Service Messages */}
            <div style={{ marginBottom: "1.5rem", background: "#f8fafc", borderRadius: 8, padding: "1rem", border: "1px solid #e0e7ef" }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Service Messages</div>
              <div style={{ fontSize: "0.97em", color: "#334155", marginBottom: 8 }}>We’ll send important updates about your quote or policy.</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={serviceMessageAgreed}
                  onChange={(e) => setServiceMessageAgreed(e.target.checked)}
                  style={{ accentColor: "#2563eb" }}
                />
                I agree to receive service-related messages by email, phone, and text.
              </label>
            </div>
            {/* Marketing Messages */}
            <label className="form-label" style={{ marginBottom: "8px", display: "block" }}>
              Communication Preferences
            </label>
            <div style={{ marginBottom: "12px", color: "#64748b", fontSize: "0.95em" }}>
              Select one preferred method for receiving marketing communications
            </div>
            {validationErrors.communicationPreference && (
              <div className="error-message" style={{ marginBottom: "12px" }}>
                {validationErrors.communicationPreference}
              </div>
            )}
            <div style={{ display: "flex", gap: "48px", alignItems: "flex-start", width: "100%" }}>
              {/* Email Opt-In/Opt-Out */}
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span>
                  Email <span className="text-danger">*</span>
                </span>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="radio"
                      name="optInEmail"
                      value="opt-in"
                      checked={optInStatus.email === "opt-in"}
                      onChange={() => handleOptInChange("email", "opt-in")}
                      style={{ accentColor: "#2563eb" }}
                    />
                    Opt-In
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="radio"
                      name="optInEmail"
                      value="opt-out"
                      checked={optInStatus.email === "opt-out"}
                      onChange={() => handleOptInChange("email", "opt-out")}
                      style={{ accentColor: "#2563eb" }}
                    />
                    Opt-Out
                  </label>
                </div>
                {validationErrors.emailOptInStatus && <span className="error-message">{validationErrors.emailOptInStatus}</span>}
                {optInStatus.email === "opt-in" && (
                  <>
                    <input
                      type="email"
                      id="optInEmail"
                      name="optInEmail"
                      className={`form-input ${validationErrors.optInEmail ? "error" : ""}`}
                      placeholder="Email for communication"
                      value={optInEmail}
                      onChange={(e) => {
                        setOptInEmail(e.target.value);
                        if (validationErrors.optInEmail) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.optInEmail;
                            return newErrors;
                          });
                        }
                      }}
                      required
                      style={{ marginTop: "8px", width: "100%" }}
                    />
                    {validationErrors.optInEmail && <span className="error-message">{validationErrors.optInEmail}</span>}
                  </>
                )}
              </div>
              {/* Phone Opt-In/Opt-Out */}
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span>
                  Phone <span className="text-danger">*</span>
                </span>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="radio"
                      name="optInPhone"
                      value="opt-in"
                      checked={optInStatus.phone === "opt-in"}
                      onChange={() => handleOptInChange("phone", "opt-in")}
                      style={{ accentColor: "#2563eb" }}
                    />
                    Opt-In
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="radio"
                      name="optInPhone"
                      value="opt-out"
                      checked={optInStatus.phone === "opt-out"}
                      onChange={() => handleOptInChange("phone", "opt-out")}
                      style={{ accentColor: "#2563eb" }}
                    />
                    Opt-Out
                  </label>
                </div>
                {validationErrors.phoneOptInStatus && <span className="error-message">{validationErrors.phoneOptInStatus}</span>}
                {optInStatus.phone === "opt-in" && (
                  <>
                    <input
                      type="tel"
                      id="optInPhone"
                      name="optInPhone"
                      className={`form-input ${validationErrors.optInPhone ? "error" : ""}`}
                      placeholder="Phone for communication"
                      value={optInPhone}
                      onChange={(e) => {
                        setOptInPhone(formatPhoneNumberDash(e.target.value));
                        if (validationErrors.optInPhone) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.optInPhone;
                            return newErrors;
                          });
                        }
                      }}
                      required
                      style={{ marginTop: "8px", width: "100%" }}
                    />
                    {validationErrors.optInPhone && <span className="error-message">{validationErrors.optInPhone}</span>}
                  </>
                )}
              </div>
              {/* Text Opt-In/Opt-Out */}
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <span>
                  Text <span className="text-danger">*</span>
                </span>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="radio"
                      name="optInText"
                      value="opt-in"
                      checked={optInStatus.text === "opt-in"}
                      onChange={() => handleOptInChange("text", "opt-in")}
                      style={{ accentColor: "#2563eb" }}
                    />
                    Opt-In
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <input
                      type="radio"
                      name="optInText"
                      value="opt-out"
                      checked={optInStatus.text === "opt-out"}
                      onChange={() => handleOptInChange("text", "opt-out")}
                      style={{ accentColor: "#2563eb" }}
                    />
                    Opt-Out
                  </label>
                </div>
                {validationErrors.textOptInStatus && <span className="error-message">{validationErrors.textOptInStatus}</span>}
                {optInStatus.text === "opt-in" && (
                  <>
                    <input
                      type="tel"
                      id="optInText"
                      name="optInText"
                      className={`form-input ${validationErrors.optInText ? "error" : ""}`}
                      placeholder="Text for communication"
                      value={optInText}
                      onChange={(e) => {
                        setOptInText(e.target.value);
                        if (validationErrors.optInText) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.optInText;
                            return newErrors;
                          });
                        }
                      }}
                      required
                      style={{ marginTop: "8px", width: "100%" }}
                    />
                    {validationErrors.optInText && <span className="error-message">{validationErrors.optInText}</span>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button className="btn btn-secondary" onClick={onPrevious}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
            </svg>
            Previous
          </button>
          {/* {!isFromReview && (
            <button className="btn btn-secondary" onClick={() => navigate("/select-quote-flow")}>
             
              Previous
            </button>
          )} */}
          {/* {isFromReview ? (
            <button className="btn btn-secondary" onClick={handlePreviousFromReview}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
              </svg>
              Back to Review
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={onPrevious}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
              </svg>
              Previous
            </button>
          )} */}
          {/* <button className="btn btn-secondary" onClick={handleClearForm}>
            
            Clear Form
          </button> */}
          {isFromReview ? (
            <button className="btn-primary" onClick={handleContinueFromReview}>
              Save & Return
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" stroke="currentColor" />
              </svg>
            </button>
          ) : (
            <button
              className="btn btn-primary"
              disabled={loading}
              onClick={() => {
                if (validateForm()) {
                  onNext &&
                    onNext({
                      ...contactData,
                      cellphone: contactData.cellphone.replace(/\D/g, ""),
                      homePhone: contactData.homePhone ? contactData.homePhone.replace(/\D/g, "") : "",
                      state: contactData.state,
                      previousSelectedState: previousSelectedState,
                      priorCarrierId: contactData.priorCarrierId,
                      serviceMessageAgreed,
                      emailOptInStatus: optInStatus.email,
                      phoneOptInStatus: optInStatus.phone,
                      textOptInStatus: optInStatus.text,
                      optInEmail: optInStatus.email === "opt-in" ? optInEmail : "",
                      optInPhone: optInStatus.phone === "opt-in" ? optInPhone.replace(/\D/g, "") : "",
                      optInText: optInStatus.text === "opt-in" ? optInText : "",
                      // Only send prior address data if yearsAtResidence < 3
                      priorAddress: Number(contactData.yearsAtResidence) < 3 ? contactData.priorAddress : "",
                      priorCity: Number(contactData.yearsAtResidence) < 3 ? contactData.priorCity : "",
                      priorState: Number(contactData.yearsAtResidence) < 3 ? contactData.priorState : "",
                      priorZipCode: Number(contactData.yearsAtResidence) < 3 ? contactData.priorZipCode : "",
                    });
                }
              }}
            >
              {loading ? (
                "Saving..."
              ) : (
                <>
                  Continue
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                  </svg>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // If called from workflow, return content without Layout wrapper
  if (onNext || currentStep) {
    return content;
  }

  // If called directly (standalone page), return with Layout wrapper
  return <Layout currentPage="Contact Information">{content}</Layout>;
}
